import { ArrowUpRight, Quote, Gift } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Reveal, Eyebrow } from "@/lib/motion";
import { FAQS, KNOWLEDGE_CATEGORIES, IMG, VIDEOS, LINKS } from "@/data/site";
import { useState } from "react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { Loader2 } from "lucide-react";

/* ---------------- About Suhani ---------------- */
export const About = () => (
  <section id="about" className="relative py-24 md:py-32 scroll-mt-20 bg-ink-soft border-y border-white/5" data-testid="about-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative max-w-sm mx-auto lg:mx-0">
          <div className="absolute -inset-4 rounded-[2rem] bg-brand/15 blur-2xl" />
          <div className="relative rounded-[2rem] overflow-hidden border border-white/10">
            <img src={IMG.founder} alt="Suhani Singh, Founder of SuhaniEdge" className="w-full aspect-square object-cover" loading="lazy" />
            <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-ink to-transparent">
              <p className="font-heading text-lg font-semibold text-white">Suhani Singh</p>
              <p className="text-sm text-brand-light">Founder, SuhaniEdge</p>
            </div>
          </div>
        </div>
      </Reveal>
      <div>
        <Reveal><Eyebrow>Meet the founder</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Meet Suhani Singh</h2></Reveal>
        <Reveal delay={0.15}><p className="mt-2 text-brand-light font-medium">Founder, SuhaniEdge — AI &amp; Digital Marketing Trainer</p></Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-lg text-slate-400 leading-relaxed">Suhani Singh is an AI and digital marketing trainer focused on helping learners understand modern technology through practical, accessible education.</p>
        </Reveal>
        <Reveal delay={0.28}>
          <ul className="mt-6 space-y-2 text-slate-300">
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand" />AI and digital marketing training</li>
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand" />Online and offline teaching</li>
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand" />1,200+ students taught</li>
          </ul>
        </Reveal>
        <Reveal delay={0.34}>
          <div className="mt-7 flex flex-wrap gap-2">
            {["Generative AI", "Prompt Engineering", "AI Image", "AI Video", "AI Content", "AI Automation", "Digital Marketing", "SEO"].map((t) => (
              <span key={t} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300">{t}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <a href="#about" data-testid="about-story-btn" className="mt-8 group inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-brand text-white font-medium px-7 py-3.5 transition-colors">
            Read My Story <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Testimonials (video reels) ---------------- */
export const Testimonials = () => (
  <section className="relative py-24 md:py-32" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Social proof</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">What Learners Say</h2></Reveal>
        <Reveal delay={0.15}><p className="mt-4 text-slate-400">Real words from real SuhaniEdge students.</p></Reveal>
      </div>
      <div className="mt-14 grid gap-6 grid-cols-2 md:grid-cols-3 max-w-4xl mx-auto">
        {VIDEOS.map((v, i) => (
          <Reveal key={i} delay={(i % 3) * 0.1}>
            <figure data-testid={`testimonial-video-${i}`} className="group relative mx-auto w-full max-w-[240px] rounded-3xl overflow-hidden border border-white/10 bg-ink-surface">
              <figcaption className="absolute top-0 inset-x-0 z-10 p-4 bg-gradient-to-b from-ink/90 to-transparent pointer-events-none">
                <p className="text-sm font-semibold text-white">{v.name}</p>
                <p className="text-xs text-brand-light">{v.program}</p>
              </figcaption>
              <video
                src={v.src}
                poster={`/posters/t${i}.jpg`}
                className="w-full aspect-[9/16] object-cover"
                controls
                playsInline
                preload="none"
              />
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Free Resources ---------------- */
export const FreeResources = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please enter your name and email."); return; }
    setLoading(true);
    try {
      await submitLead({ type: "free_kit", name: form.name, email: form.email });
      toast.success("Check your inbox soon — your AI Starter Kit is on the way!");
      setForm({ name: "", email: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <section id="resources" className="relative py-24 md:py-32 scroll-mt-20 bg-ink-soft border-y border-white/5" data-testid="resources-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="rounded-[2rem] border border-white/10 glass p-6 md:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 rounded-[2rem] bg-brand/15 blur-2xl" />
            <img src={IMG.starterKit} alt="SuhaniEdge AI Starter Kit — premium digital guide" className="relative rounded-2xl border border-white/10 w-full object-cover shadow-[0_20px_60px_rgba(0,0,0,0.5)]" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow>Free AI Resources</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight">Start With Free AI Resources</h2>
            <p className="mt-4 text-slate-400"><span className="text-white font-medium">SuhaniEdge AI Starter Kit</span> — a practical collection of beginner-friendly AI tools, prompts and workflows.</p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="free-kit-form">
              <Input data-testid="free-kit-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className="bg-ink border-white/10 text-white h-12" />
              <Input data-testid="free-kit-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@email.com" className="bg-ink border-white/10 text-white h-12" />
              <button type="submit" data-testid="free-kit-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand hover:bg-brand-light text-white font-semibold py-3.5 transition-colors disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Get the Free Kit
              </button>
            </form>
            <div className="my-5 flex items-center gap-3 text-xs text-slate-500">
              <span className="h-px flex-1 bg-white/10" /> OR <span className="h-px flex-1 bg-white/10" />
            </div>
            <a href={LINKS.freeIncomeKit} target="_blank" rel="noopener noreferrer" data-testid="free-income-kit-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand/50 bg-brand/10 text-brand-light hover:bg-brand hover:text-white font-semibold py-3.5 transition-colors">
              <Gift className="h-4 w-4" /> Get the Free AI Income Kit
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Knowledge Hub ---------------- */
export const KnowledgeHub = () => (
  <section className="relative py-24 md:py-32" data-testid="knowledge-hub">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <Eyebrow>Knowledge Hub</Eyebrow>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">The SuhaniEdge AI Knowledge Hub</h2>
        </div>
        <a href={LINKS.blog} target="_blank" rel="noopener noreferrer" data-testid="knowledge-explore-btn" className="group inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-brand text-white font-medium px-6 py-3 transition-colors whitespace-nowrap">
          Explore the Knowledge Hub <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {KNOWLEDGE_CATEGORIES.map((c, i) => (
          <Reveal key={c} delay={(i % 4) * 0.06}>
            <a href={LINKS.blog} target="_blank" rel="noopener noreferrer" data-testid={`hub-card-${i}`} className="group block h-full rounded-3xl border border-white/5 bg-ink-surface p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-light font-semibold">{c}</p>
              <h3 className="mt-4 font-heading text-lg font-medium text-white">Guides &amp; articles on {c}</h3>
              <p className="mt-3 text-sm text-slate-500 group-hover:text-slate-400 transition-colors">Read on the SuhaniEdge blog →</p>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
export const Faq = () => (
  <section className="relative py-24 md:py-32 bg-ink-soft border-y border-white/5" data-testid="faq-section">
    <div className="mx-auto max-w-3xl px-6 lg:px-10">
      <div className="text-center">
        <Eyebrow className="justify-center">FAQ</Eyebrow>
        <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Questions, answered.</h2>
      </div>
      <Accordion type="single" collapsible className="mt-12 space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="rounded-2xl border border-white/10 bg-ink px-6 overflow-hidden">
            <AccordionTrigger className="text-left font-heading text-lg font-medium text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-400 leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ---------------- Final CTA ---------------- */
export const FinalCTA = () => (
  <section className="relative py-28 md:py-40 overflow-hidden" data-testid="final-cta">
    <div className="absolute inset-0 bg-gradient-to-br from-brand-dark via-brand to-brand-dark" />
    <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4), transparent 40%)" }} />
    <div className="relative mx-auto max-w-4xl px-6 lg:px-10 text-center">
      <Reveal>
        <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold text-white tracking-tighter">Your AI Edge Starts Here.</h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl mx-auto">Learn the tools. Build the skills. Apply them in the real world.</p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="#academy" data-testid="cta-academy-btn" className="inline-flex items-center gap-2 rounded-full bg-white text-brand-dark font-semibold px-8 py-4 hover:bg-white/90 transition-colors">Explore SuhaniEdge Academy</a>
          <a href={LINKS.connect} target="_blank" rel="noopener noreferrer" data-testid="cta-coaching-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 hover:border-white text-white font-medium px-8 py-4 transition-colors">Book Coaching</a>
        </div>
      </Reveal>
    </div>
  </section>
);
